/**
 * Toaster — app-wide toasts. Listens for the existing window 'toast' event
 * (utils/toast.showToast(text, type)), so every caller keeps working. Toasts
 * spring in, stack, and slide away; hover pauses the timer; click dismisses.
 * Top-right on desktop, bottom-center on phones. Mounted once in App.js.
 *
 * Types: 'success' | 'info' | 'error' | 'warning'.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Text, Pressable, Animated, StyleSheet, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { IS_WEB, spring, timing, webTransition } from '../../utils/motion';

const ICONS = {
    success: { name: 'checkmark-circle', color: '#34D399' },
    error: { name: 'alert-circle', color: '#F87171' },
    warning: { name: 'warning', color: '#FBBF24' },
    info: { name: 'information-circle', color: '#A5B4FC' },
};
const LIFETIME_MS = 2600;
const MAX_TOASTS = 4;

function ToastItem({ toast, onDone, mobile }) {
    const t = useRef(new Animated.Value(0)).current;
    const timer = useRef(null);
    const leaving = useRef(false);

    const leave = useCallback(() => {
        if (leaving.current) return;
        leaving.current = true;
        timing(t, 0, { duration: 'base', easing: 'in', useNative: false }).start(() => onDone(toast.id));
    }, [onDone, t, toast.id]);

    const arm = useCallback(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(leave, toast.duration || LIFETIME_MS);
    }, [leave, toast.duration]);

    useEffect(() => {
        spring(t, 1, 'pop', { useNative: false }).start();
        arm();
        return () => clearTimeout(timer.current);
    }, [arm, t]);

    const icon = ICONS[toast.type] || ICONS.info;
    const offset = mobile ? 16 : 24;
    return (
        <Animated.View
            style={{
                opacity: t,
                transform: [
                    { translateY: t.interpolate({ inputRange: [0, 1], outputRange: [mobile ? offset : -offset / 2, 0] }) },
                    { scale: t.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1] }) },
                ],
            }}
        >
            <Pressable
                onPress={leave}
                onHoverIn={() => clearTimeout(timer.current)}
                onHoverOut={arm}
                role="status"
                style={({ hovered }) => [styles.toast, hovered && styles.toastHover, webTransition(['background-color', 'border-color'], 'fast')]}
            >
                <Ionicons name={icon.name} size={18} color={icon.color} />
                <Text style={styles.text} numberOfLines={3}>{toast.text}</Text>
            </Pressable>
        </Animated.View>
    );
}

export default function Toaster() {
    const [toasts, setToasts] = useState([]);
    const { width } = useWindowDimensions();
    const mobile = width < 640;

    useEffect(() => {
        if (!IS_WEB || typeof window === 'undefined') return undefined;
        let n = 0;
        const handler = (e) => {
            const d = e?.detail || {};
            if (!d.text) return;
            const id = `${Date.now()}-${n++}`;
            setToasts((prev) => {
                // The same message twice in a row refreshes instead of stacking.
                const rest = prev.filter((x) => x.text !== d.text);
                return [...rest, { id, type: d.type || 'info', text: String(d.text), duration: d.duration }].slice(-MAX_TOASTS);
            });
        };
        window.addEventListener('toast', handler);
        return () => window.removeEventListener('toast', handler);
    }, []);

    const remove = useCallback((id) => setToasts((prev) => prev.filter((x) => x.id !== id)), []);
    if (!toasts.length) return null;
    return (
        <View style={[styles.host, mobile ? styles.hostMobile : styles.hostDesktop, { pointerEvents: 'box-none' }]} aria-live="polite">
            {toasts.map((x) => <ToastItem key={x.id} toast={x} onDone={remove} mobile={mobile} />)}
        </View>
    );
}

const styles = StyleSheet.create({
    host: { position: IS_WEB ? 'fixed' : 'absolute', zIndex: 10000, gap: 8 },
    hostDesktop: { top: 108, right: 24, alignItems: 'flex-end', maxWidth: 380 },
    hostMobile: { bottom: 24, left: 16, right: 16, alignItems: 'stretch', flexDirection: 'column-reverse' },
    toast: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 11,
        paddingHorizontal: 14,
        borderRadius: 12,
        backgroundColor: 'rgba(24,22,30,0.92)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.12)',
        ...(IS_WEB ? { backdropFilter: 'blur(14px)', boxShadow: '0 10px 30px rgba(0,0,0,0.45)', cursor: 'pointer' } : {}),
    },
    toastHover: { backgroundColor: 'rgba(32,29,40,0.95)', borderColor: 'rgba(255,255,255,0.2)' },
    text: { color: 'rgba(255,255,255,0.92)', fontSize: 13, fontWeight: '600', flexShrink: 1 },
});
